import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AccountSettings, defaultAccountSettings } from 'app/models/api/account-settings';
import { combineLatest, map, Observable } from 'rxjs';
import { StorageService, StorageType } from './storage/storage.service';
import { API_ENDPOINT } from './utils.service';
import { Identity, SapLanguage, SapUser, WebPubSubRequest, WebPubSubService } from '@festo-ax/smartenance-shared-ui';

export interface ProfileResult {
  profileId?: string;
  profileName?: string;
  hashedPin?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly storageService: StorageService = inject(StorageService);
  private readonly webPubSubService: WebPubSubService = inject(WebPubSubService);

  private readonly accountSettingsStorageKey = 'ecn-stored-account-settings';
  private readonly PROFILE_API = `${API_ENDPOINT}/profile`;

  getResetApprovals(): Observable<ProfileResult[]> {
    return this.http.get<ProfileResult[]>(`${this.PROFILE_API}/reset-approvals`);
  }

  verifyReset(profileId: string, allow: boolean): Observable<void> {
    return this.http.put<void>(`${this.PROFILE_API}/${profileId}/verify-reset`, { allow });
  }

  getAccountSetting(): AccountSettings | null {
    return this.storageService.get<AccountSettings>(this.accountSettingsStorageKey, StorageType.Local);
  }

  setUpcomingTasksDialogAvailabilityOnCompleteSetting(value: boolean): void {
    const accountSettings: AccountSettings = this.storageService.get<AccountSettings>(
      this.accountSettingsStorageKey,
      StorageType.Local
    ) ?? { ...defaultAccountSettings };
    accountSettings.upcomingTasksDialogAvailabilityOnCompleteSetting = value;
    this.storageService.set<AccountSettings>(this.accountSettingsStorageKey, accountSettings, StorageType.Local);
  }

  // todo remove with bunch of changes provided, SapUser is a deprecated interface
  loadSapData(identity: Identity, sapLanguage?: string): Observable<{ sapLanguages: SapLanguage[]; sapUsers: SapUser[] }> {
    const sapUsersRequest: WebPubSubRequest<'getAllSapUsers'> = { topic: 'getAllSapUsers', data: { language: sapLanguage } };
    const sapLanguagesRequest: WebPubSubRequest<'getSupportedSapLanguages'> = {
      topic: 'getSupportedSapLanguages',
      data: { language: sapLanguage }
    };

    return combineLatest([
      this.webPubSubService.sendRequest(sapUsersRequest, identity),
      this.webPubSubService.sendRequest(sapLanguagesRequest, identity)
    ]).pipe(
      map(([sapUsersResponse, sapLanguagesResponse]) => ({
        sapLanguages: sapLanguagesResponse.data?.languages || [],
        sapUsers: sapUsersResponse.data?.users || []
      }))
    );
  }
}
